import { useEffect, useRef, lazy, Suspense } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useMobile } from './hooks/useMobile';
import { useReducedMotion } from './hooks/useReducedMotion';
import { useScrollStore } from '@/stores/scrollStore';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

const PsychologyConstellation = lazy(() => import('./3d/PsychologyConstellation'));

gsap.registerPlugin(ScrollTrigger);

const researchers = [
  {
    name: 'Robert Cialdini',
    contribution: 'Principles of Persuasion',
    color: '#7C3AED',
  },
  {
    name: 'Daniel Kahneman',
    contribution: 'Cognitive Biases & Decision Making',
    color: '#60A5FA',
  },
  {
    name: 'Amos Tversky',
    contribution: 'Prospect Theory & Loss Aversion',
    color: '#34D399',
  },
];

const principles = [
  'Scarcity',
  'Social Proof',
  'Anchoring',
  'Loss Aversion',
  'Reciprocity',
  'Authority',
  'Commitment',
  'Framing',
  'Bandwagon',
  'Curiosity Gap',
  'Peak-End Rule',
  'Status Quo Bias',
  'Mere Exposure',
  'Cognitive Ease',
  'Contrast Effect',
];

export function PsychologyEngineSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useMobile();
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    if (prefersReduced || !sectionRef.current) return;

    const ctx = gsap.context(() => {
      // Pin section for 2x viewport scroll
      if (!isMobile) {
        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=200%',
          pin: true,
          scrub: 1,
          onUpdate: (self) => {
            useScrollStore.getState().setPsychProgress(self.progress);
          },
        });
      }

      // Text entrance
      gsap.from('.psych-content > *', {
        opacity: 0,
        y: 30,
        stagger: 0.1,
        duration: 0.7,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.psych-content',
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });

      // Researcher cards stagger
      gsap.from('.researcher-card', {
        opacity: 0,
        y: 20,
        scale: 0.95,
        stagger: 0.12,
        duration: 0.6,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.researcher-cards',
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
      });

      // Principle nodes stagger (desktop 3D placeholder area)
      if (!isMobile) {
        gsap.from('.principle-node', {
          scale: 0,
          opacity: 0,
          stagger: 0.06,
          duration: 0.5,
          ease: 'back.out(1.5)',
          scrollTrigger: {
            trigger: '.principle-grid',
            start: 'top 80%',
            toggleActions: 'play none none none',
          },
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, [isMobile, prefersReduced]);

  return (
    <section
      ref={sectionRef}
      id="psychology"
      className="relative min-h-screen overflow-hidden bg-landing-bg"
      style={{
        paddingTop: 'clamp(80px, 10vh, 160px)',
        paddingBottom: 'clamp(80px, 10vh, 160px)',
      }}
    >
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        <div className={cn('flex gap-12', isMobile ? 'flex-col' : 'flex-row items-start')}>
          {/* Left side: text content */}
          <div className={cn('psych-content', isMobile ? 'w-full' : 'w-[35%] flex-shrink-0')}>
            <p
              className="mb-4 text-zupa-purple-light"
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 'clamp(0.9rem, 1vw, 1.1rem)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.2em',
              }}
            >
              The Differentiator
            </p>

            <h2
              className="mb-6 text-[#E8E6F0]"
              style={{
                fontFamily: "'Space Grotesk', system-ui, sans-serif",
                fontSize: 'clamp(2rem, 4vw + 0.5rem, 3.75rem)',
                fontWeight: 600,
                lineHeight: 1.1,
              }}
            >
              Powered by Psychology,
              <br />
              Not Just Prompts
            </h2>

            <p
              className="mb-10 max-w-[50ch] text-[#C8C6D2]"
              style={{
                fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                fontSize: 'clamp(1.25rem, 1vw + 0.9rem, 1.5rem)',
                lineHeight: 1.65,
              }}
            >
              Every post ZUPA generates is engineered using 15+ research-backed psychological
              principles — Scarcity, Social Proof, Anchoring, Loss Aversion, and more. You see exactly which
              principles were applied and why they work.
            </p>

            {/* Researcher cards */}
            <div className="researcher-cards space-y-4">
              {researchers.map((r) => (
                <div
                  key={r.name}
                  className="researcher-card p-5 backdrop-blur-lg"
                  style={{
                    background: 'rgba(13, 13, 26, 0.7)',
                    border: '1px solid rgba(232, 230, 240, 0.08)',
                    borderLeft: `3px solid ${r.color}`,
                  }}
                >
                  <h4
                    className="text-[#E8E6F0]"
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '1rem',
                      fontWeight: 500,
                    }}
                  >
                    {r.name}
                  </h4>
                  <p
                    className="mt-1 text-[#C8C6D2]"
                    style={{
                      fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                      fontSize: '1rem',
                    }}
                  >
                    {r.contribution}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right side: 3D constellation on desktop, principle nodes on mobile */}
          <div className={cn(isMobile ? 'w-full' : 'w-[65%] relative min-h-[850px]')}>
            {!isMobile ? (
              <Suspense fallback={<div className="w-full min-h-[600px]" />}>
                <PsychologyConstellation />
              </Suspense>
            ) : (
              <div
                className="principle-grid relative flex flex-wrap items-center justify-center gap-3"
              >
                {principles.map((p) => (
                  <motion.div
                    key={p}
                    className="principle-node"
                    whileHover={{
                      scale: 1.1,
                      borderColor: 'rgba(124, 58, 237, 0.6)',
                      boxShadow: '0 0 20px rgba(124, 58, 237, 0.3)',
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <div
                      className="cursor-pointer px-4 py-2 text-center transition-colors duration-200"
                      style={{
                        background: 'rgba(13, 13, 26, 0.7)',
                        border: '1px solid rgba(232, 230, 240, 0.08)',
                        backdropFilter: 'blur(8px)',
                      }}
                    >
                      <span
                        className="text-[#E8E6F0]/80 hover:text-zupa-purple-light"
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: '0.95rem',
                          fontWeight: 500,
                        }}
                      >
                        {p}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
